import React from 'react'
import styled from 'styled-components'
import moment from 'moment-timezone'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import Published from 'Event/Dashboard/editor/views/Published'
import {useEditMode} from 'Event/EditModeProvider'
import {AbsoluteLink} from 'lib/ui/link/AbsoluteLink'
import {useAttendeeVariables} from 'Event'
import {
  Agenda,
  AgendaListProps,
} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/AgendaList'
import {Draggable} from 'react-beautiful-dnd'
import {DraggableOverlay} from 'lib/ui/drag-and-drop'
import {useCardsTemplate} from 'Event/template/Cards'
import {useToggle} from 'lib/toggle'
import {AgendaItemConfig} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/AgendaList/AgendaItemConfig'
import {useLocalization} from 'lib/LocalizationProvider'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import {useAgendaUrl} from 'Event/Dashboard/components/agneda'
import VisibleOnMatch from 'Event/attendee-rules/VisibleOnMatch'

export default function AgendaItem(props: {
  agenda: Agenda
  index: number
  list: AgendaListProps
  id: string
  listId: string
}) {
  const {agenda, index, id, listId} = props
  const isEditMode = useEditMode()
  const {flag: configVisible, toggle: toggleConfig} = useToggle()
  const {flag: showingCopyConfig, toggle: toggleCopyConfig} = useToggle()

  if (!isEditMode) {
    return (
      <VisibleOnMatch rules={agenda.rules}>
        <Published component={agenda}>
          <Box aria-label="agenda">
            <Event agenda={agenda} />
            <Times agenda={agenda} />
          </Box>
        </Published>
      </VisibleOnMatch>
    )
  }

  return (
    <>
      <EditModeOnly>
        <AgendaItemConfig
          showing={configVisible}
          onClose={toggleConfig}
          agenda={agenda}
          id={id}
          listId={listId}
        />
        <AgendaItemConfig
          showing={showingCopyConfig}
          onClose={toggleCopyConfig}
          agenda={agenda}
          listId={listId}
        />
      </EditModeOnly>
      <Draggable draggableId={id} index={index}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.draggableProps}>
            <DraggableOverlay>
              <Editable
                onEdit={toggleConfig}
                onCopy={toggleCopyConfig}
                key={index}
                handleProps={provided.dragHandleProps}
              >
                <>
                  <VisibleOnMatch rules={agenda.rules}>
                    <Published component={agenda}>
                      <Box aria-label="agenda" style={{minHeight: 30}}>
                        <Event agenda={agenda} />
                        <Times agenda={agenda} />
                      </Box>
                    </Published>
                  </VisibleOnMatch>
                </>
              </Editable>
            </DraggableOverlay>
          </div>
        )}
      </Draggable>
    </>
  )
}

const Box = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[3]};
`

function Times(props: {agenda: Agenda}) {
  const start = moment(props.agenda.startDate).tz(moment.tz.guess())
  const getMonth = (date: moment.Moment) => date.format('MMMM')
  const getDay = (date: moment.Moment) => date.format('Do')

  const {has12HourTime} = useLocalization()
  const timeFormat = has12HourTime ? 'h:mma' : 'HH:mm'
  const getTime = (date: moment.Moment) => date.format(timeFormat)

  const getTimezone = (date: moment.Moment) =>
    date.tz(moment.tz.guess()).format('z')
  const {sidebar} = useCardsTemplate()
  const sidebarTextColor = sidebar.textColor

  const tz = getTimezone(start)

  if (!props.agenda.startDate) return null

  /**
   * Single start date only
   */
  if (
    !props.agenda.endDate ||
    props.agenda.startDate === props.agenda.endDate
  ) {
    return (
      <TimeText aria-label="agenda event times" textColor={sidebarTextColor}>
        <strong>{`${getMonth(start)} ${getDay(start)}:`}</strong>{' '}
        {`${getTime(start)} ${tz}`}
      </TimeText>
    )
  }

  /**
   * Start, and end same day, different times
   */
  const end = moment(props.agenda.endDate).tz(moment.tz.guess())
  const sameMonth = getMonth(end) === getMonth(start)
  const sameDay = getDay(end) === getDay(start)
  if (sameMonth && sameDay) {
    return (
      <TimeText aria-label="agenda event times" textColor={sidebarTextColor}>
        <strong>{`${getMonth(start)} ${getDay(start)}:`}</strong>{' '}
        {getTime(start)}
        {` - ${getTime(end)} ${tz}`}
      </TimeText>
    )
  }

  /**
   * Two different date/times
   */
  return (
    <TimeText aria-label="agenda event times" textColor={sidebarTextColor}>
      <strong>{`${getMonth(start)} ${getDay(start)}:`}</strong> {getTime(start)}
      {` - `}
      <strong>
        {getMonth(end)} {getDay(end)}:
      </strong>{' '}
      {getTime(end)} {tz}
    </TimeText>
  )
}

function Event(props: {agenda: Agenda}) {
  const {sidebar} = useCardsTemplate()
  const v = useAttendeeVariables()

  const agendaLink = useAgendaUrl(props.agenda)

  if (!v(props.agenda.text)) return null

  if (agendaLink) {
    return (
      <StyledAbsoluteLink newTab to={agendaLink} color={sidebar.textColor}>
        <EventText aria-label="agenda event" textColor={sidebar.textColor}>
          {v(props.agenda.text)}
        </EventText>
      </StyledAbsoluteLink>
    )
  }

  return (
    <EventText aria-label="agenda event" textColor={sidebar.textColor}>
      {v(props.agenda.text)}
    </EventText>
  )
}

const TimeText = styled.span<{textColor: string | null}>`
  font-size: 14px;
  color: ${(props) => props.color};
`

const EventText = styled.span<{textColor: string | null}>`
  font-size: 18px;
  display: block;
  font-style: italic;
  color: ${(props) => props.color};
`

const StyledAbsoluteLink = styled(AbsoluteLink)<{color: string | null}>`
  color: ${(props) => props.color};

  &:hover {
    color: ${(props) => props.color} !important;
  }
`
